import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, RotateCcw, ChevronDown, SkipForward, Bell, BellOff } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

// ────────────────────────────────────────────────────────────────────────────
// TECHNIQUE LIBRARY
// Two shapes are supported:
//   1. `phases` — cycle-based: a fixed array of phases looped indefinitely.
//   2. `rounds` — round-based: a linear list of rounds, each containing
//      ordered "segments" with their own duration. Segments can be:
//        - type 'active'  → loops an inner `pattern` for `duration` seconds
//        - type 'hold'    → static circle (breath: 'empty' or 'full') for `duration`
//        - type 'recover' → slow natural visualization for `duration`
//      When a technique has `rounds`, the round engine renders instead of the
//      cycle engine. The other 7 techniques keep their original behaviour.
// ────────────────────────────────────────────────────────────────────────────

const COLOR_IN = '#C6B28F';
const COLOR_OUT = '#8B7E6A';
const COLOR_HOLD = '#7A8274';
const COLOR_REST = '#6B6A65';

const BREATH_OF_FIRE_PATTERN = [
  { name: 'Inhale', duration: 1.5, color: COLOR_IN },
  { name: 'Exhale', duration: 1.5, color: COLOR_OUT },
];

const makeBoFRound = (label, activeSec, exhaleHoldSec, inhaleHoldSec = 15, recoverSec = 30) => ({
  label,
  segments: [
    {
      id: 'active',
      label: 'Active Breathing',
      type: 'active',
      duration: activeSec,
      color: COLOR_IN,
      pattern: BREATH_OF_FIRE_PATTERN,
      instruction: 'Rapid passive inhales, forceful exhales through the nose. Belly pumps.',
    },
    {
      id: 'hold_empty',
      label: 'Exhale Hold',
      type: 'hold',
      breath: 'empty',
      duration: exhaleHoldSec,
      color: COLOR_REST,
      instruction: 'Lungs empty. Surrender. Notice what arises.',
    },
    {
      id: 'hold_full',
      label: 'Inhale Hold',
      type: 'hold',
      breath: 'full',
      duration: inhaleHoldSec,
      color: COLOR_IN,
      instruction: 'Peak inhale. Hold at the top.',
    },
    {
      id: 'recover',
      label: 'Recovery',
      type: 'recover',
      duration: recoverSec,
      color: COLOR_HOLD,
      instruction: 'Slow natural breaths. Integration.',
    },
  ],
});

const TECHNIQUES = [
  {
    id: 'sigh',
    name: 'Physiological Sigh',
    description: 'Two short inhales through the nose, one long exhale through the mouth — fastest vagal Reset',
    phases: [
      { name: 'Inhale', duration: 2, color: COLOR_IN },
      { name: 'Top-up', duration: 1, color: COLOR_IN },
      { name: 'Exhale', duration: 6, color: COLOR_OUT },
      { name: 'Rest', duration: 1, color: COLOR_REST },
    ],
  },
  {
    id: 'box',
    name: 'Box Breathing',
    description: 'Equal counts for calm and balance',
    phases: [
      { name: 'Inhale', duration: 4, color: COLOR_IN },
      { name: 'Hold', duration: 4, color: COLOR_HOLD },
      { name: 'Exhale', duration: 4, color: COLOR_OUT },
      { name: 'Hold', duration: 4, color: COLOR_HOLD },
    ],
  },
  {
    id: '478',
    name: '4-7-8 Technique',
    description: 'Deep relaxation and nervous system reset',
    phases: [
      { name: 'Inhale', duration: 4, color: COLOR_IN },
      { name: 'Hold', duration: 7, color: COLOR_HOLD },
      { name: 'Exhale', duration: 8, color: COLOR_OUT },
    ],
  },
  {
    id: 'resonant',
    name: 'Resonant Breathing',
    description: 'Heart-coherent rhythm at 5.5 breaths per minute',
    phases: [
      { name: 'Inhale', duration: 5.5, color: COLOR_IN },
      { name: 'Exhale', duration: 5.5, color: COLOR_OUT },
    ],
  },
  {
    id: 'energizing',
    name: 'Something Wonderful Is Happening',
    description: 'Fast, rhythmic breathing while inwardly declaring "something wonderful is happening" — after Neville Goddard. Activates the assumption that the new is already arriving.',
    phases: [
      { name: 'Inhale', duration: 2, color: COLOR_IN },
      { name: 'Exhale', duration: 2, color: COLOR_OUT },
    ],
  },
  {
    id: 'embodiment',
    name: 'Identity Embodiment',
    description: 'Slow deep breaths to anchor into your new identity',
    phases: [
      { name: 'Inhale', duration: 6, color: COLOR_IN },
      { name: 'Hold', duration: 3, color: COLOR_HOLD },
      { name: 'Exhale', duration: 8, color: COLOR_OUT },
      { name: 'Rest', duration: 2, color: COLOR_REST },
    ],
  },
  {
    id: 'breath_of_fire',
    name: 'Breath of Fire for Peace and Power',
    description: 'DMT alkaline breathing in guided rounds. Rapid active breathing → exhale hold → inhale hold → recovery. ⚠ Skip if pregnant, have heart issues, or panic disorder.',
    rounds: [
      makeBoFRound('Round 1', 60, 60, 15, 30),  // 165s
      makeBoFRound('Round 2', 75, 90, 15, 30),  // 210s
      makeBoFRound('Round 3', 90, 120, 15, 30), // 255s
    ], // total ~10.5 min
  },
];

// ────────────────────────────────────────────────────────────────────────────
// SOFT CHIME — synthesized via Web Audio API, no asset files.
// A gentle two-tone bell played at every segment transition + completion.
//
// iOS Safari quirk: a fresh AudioContext starts in "suspended" state and can
// ONLY be resumed in direct response to a user gesture. We therefore keep a
// single shared context for the whole page and unlock it on the user's first
// "Start"/test click. Subsequent chimes (which happen inside setInterval) then
// fire reliably on iOS, iPadOS, and Safari desktop.
// ────────────────────────────────────────────────────────────────────────────

let _sharedCtx = null;
let _ctxUnlocked = false;

function _getCtx() {
  if (_sharedCtx) return _sharedCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try { _sharedCtx = new Ctx(); } catch { return null; }
  return _sharedCtx;
}

/**
 * Unlock the AudioContext. MUST be called from inside a user-gesture handler
 * (click/tap/keydown) — otherwise iOS Safari leaves it suspended and chimes
 * play silently. Safe to call multiple times.
 *
 * Also plays a near-silent burst on the very first unlock so iOS marks the
 * context as user-initiated for the rest of the session.
 */
export function unlockChimeAudio() {
  const ctx = _getCtx();
  if (!ctx) return false;
  if (ctx.state === 'suspended') {
    // resume() returns a Promise; we don't await because the next chime will
    // either fire (good) or be silently dropped (acceptable). The unlock burst
    // below covers iOS's "needs immediate sound after gesture" requirement.
    ctx.resume().catch(() => { /* noop */ });
  }
  if (!_ctxUnlocked) {
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0002, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.02);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.03);
      _ctxUnlocked = true;
    } catch { /* noop */ }
  }
  return ctx.state === 'running';
}

function playChime(variant = 'segment') {
  try {
    const ctx = _getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // Try to resume — only succeeds if we're still inside a recent gesture.
      ctx.resume().catch(() => { /* noop */ });
    }
    const now = ctx.currentTime;

    // Two soft bell tones — completion uses a higher, brighter pair.
    const tones = variant === 'completion'
      ? [
          { freq: 880, when: 0,    dur: 1.6 },  // A5
          { freq: 1175, when: 0.18, dur: 1.6 }, // D6
        ]
      : [
          { freq: 528, when: 0,    dur: 1.1 },  // soft "C5+"
          { freq: 660, when: 0.14, dur: 1.1 },  // E5
        ];

    tones.forEach(({ freq, when, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      // Pluck-like envelope: instant attack, exponential decay.
      gain.gain.setValueAtTime(0.0001, now + when);
      gain.gain.exponentialRampToValueAtTime(0.22, now + when + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + when + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + when);
      osc.stop(now + when + dur + 0.05);
    });
  } catch {
    // Silent — audio is non-essential.
  }
}

// ────────────────────────────────────────────────────────────────────────────
// PAGE
// ────────────────────────────────────────────────────────────────────────────

export default function BreathworkPage() {
  const [technique, setTechnique] = useState(TECHNIQUES[0]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] -mt-4" data-testid="breathwork-page">
      <Header technique={technique} onSelect={setTechnique} />
      {technique.rounds
        ? <RoundEngine key={technique.id} technique={technique} />
        : <CycleEngine key={technique.id} technique={technique} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// HEADER (shared)
// ────────────────────────────────────────────────────────────────────────────

function Header({ technique, onSelect }) {
  return (
    <div className="mb-10 animate-fade-up text-center px-4">
      <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Sacred Breath</p>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading mb-4">Breathwork</h1>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            data-testid="technique-selector"
            className="rounded-xl border-gold/20 bg-bone/40 hover:bg-bone font-sans text-sm gold-border-glow"
          >
            {technique.name} <ChevronDown size={14} className="ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80 bg-bone border-gold/15 rounded-2xl p-1 max-h-[70vh] overflow-y-auto">
          {TECHNIQUES.map((t) => (
            <DropdownMenuItem
              key={t.id}
              onClick={() => onSelect(t)}
              data-testid={`technique-${t.id}`}
              className={`rounded-xl p-3 cursor-pointer ${technique.id === t.id ? 'bg-gold/10' : ''}`}
            >
              <div>
                <p className="font-serif text-base text-dark">{t.name}</p>
                <p className="text-text-muted font-sans text-xs mt-0.5">{t.description}</p>
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CYCLE ENGINE — original loop-forever engine for the 7 traditional techniques
// ────────────────────────────────────────────────────────────────────────────

function CycleEngine({ technique }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const intervalRef = useRef(null);
  const phaseStartRef = useRef(null);

  const currentPhase = technique.phases[phaseIndex];

  const getCircleScale = useCallback(() => {
    if (!isPlaying && cycleCount === 0) return 0.6;
    const phaseName = currentPhase.name.toLowerCase();
    if (phaseName === 'inhale' || phaseName === 'top-up') return 0.6 + 0.4 * phaseProgress;
    if (phaseName === 'exhale') return 1.0 - 0.4 * phaseProgress;
    if (phaseName === 'hold') return phaseIndex === 1 ? 1.0 : 0.6;
    if (phaseName === 'rest') return 0.6;
    return 0.8;
  }, [isPlaying, cycleCount, currentPhase, phaseProgress, phaseIndex]);

  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    phaseStartRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - phaseStartRef.current) / 1000;
      const phaseDuration = technique.phases[phaseIndex].duration;
      const progress = Math.min(elapsed / phaseDuration, 1);
      setPhaseProgress(progress);
      setTotalSeconds(prev => prev + 0.05);
      if (elapsed >= phaseDuration) {
        const next = (phaseIndex + 1) % technique.phases.length;
        if (next === 0) setCycleCount(prev => prev + 1);
        setPhaseIndex(next);
        setPhaseProgress(0);
        phaseStartRef.current = Date.now();
      }
    }, 50);
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, phaseIndex, technique]);

  const togglePlay = () => {
    if (!isPlaying) {
      phaseStartRef.current = Date.now();
      // Unlock Web Audio in direct response to this click — required by iOS.
      unlockChimeAudio();
    }
    setIsPlaying(!isPlaying);
  };
  const reset = () => {
    setIsPlaying(false);
    setPhaseIndex(0);
    setPhaseProgress(0);
    setCycleCount(0);
    setTotalSeconds(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const scale = getCircleScale();
  const countdown = Math.max(0, Math.ceil(currentPhase.duration - currentPhase.duration * phaseProgress));
  const formattedTime = `${Math.floor(totalSeconds / 60)}:${String(Math.floor(totalSeconds % 60)).padStart(2, '0')}`;

  return (
    <>
      <BreathingCircle
        color={currentPhase.color}
        scale={scale}
        transitionDuration={currentPhase.duration}
        primaryLabel={isPlaying ? currentPhase.name : (cycleCount > 0 ? 'Complete' : 'Ready')}
        countdown={isPlaying ? countdown : null}
      />
      <Controls isPlaying={isPlaying} onTogglePlay={togglePlay} onReset={reset} />
      <div className="flex items-center gap-8 mt-8 animate-fade-up delay-300" data-testid="breathwork-stats">
        <Stat label="Cycles" value={cycleCount} testId="cycle-count" />
        <Divider />
        <Stat label="Time" value={formattedTime} testId="total-time" />
        <Divider />
        <Stat label="Pattern" value={technique.phases.map(p => p.duration).join('-')} valueClass="text-gold text-sm" testId="pattern-display" />
      </div>
      <div className="flex items-center gap-2 mt-6 animate-fade-up delay-400 flex-wrap justify-center px-4" data-testid="phase-indicators">
        {technique.phases.map((phase, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{
                backgroundColor: phaseIndex === i && isPlaying ? phase.color : `${phase.color}40`,
                boxShadow: phaseIndex === i && isPlaying ? `0 0 12px ${phase.color}60` : 'none',
                transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
              }}
            />
            <span className={`font-sans text-xs ${phaseIndex === i && isPlaying ? 'text-dark' : 'text-text-muted'}`}>
              {phase.name} {phase.duration}s
            </span>
            {i < technique.phases.length - 1 && <span className="text-text-muted/30 mx-1">|</span>}
          </div>
        ))}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ROUND ENGINE — for Breath of Fire and other multi-segment guided practices
// ────────────────────────────────────────────────────────────────────────────

function RoundEngine({ technique }) {
  const rounds = technique.rounds;
  const [isPlaying, setIsPlaying] = useState(false);
  const [roundIndex, setRoundIndex] = useState(0);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [segmentElapsed, setSegmentElapsed] = useState(0);
  // For 'active' segments — sub-pattern position
  const [subIndex, setSubIndex] = useState(0);
  const [subProgress, setSubProgress] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    try { return localStorage.getItem('breathwork_chime') !== 'off'; } catch { return true; }
  });

  const intervalRef = useRef(null);
  const segStartRef = useRef(null);
  const subStartRef = useRef(null);

  const toggleChime = () => {
    setChimeEnabled(v => {
      const next = !v;
      try { localStorage.setItem('breathwork_chime', next ? 'on' : 'off'); } catch { /* noop */ }
      return next;
    });
  };

  const totalDurationSec = useMemo(
    () => rounds.reduce((s, r) => s + r.segments.reduce((ss, sg) => ss + sg.duration, 0), 0),
    [rounds]
  );

  const round = rounds[roundIndex];
  const segment = round?.segments[segmentIndex];

  const advance = useCallback(() => {
    // Move to next segment or next round.
    if (segmentIndex + 1 < round.segments.length) {
      if (chimeEnabled) playChime('segment');
      setSegmentIndex(segmentIndex + 1);
      setSegmentElapsed(0);
      setSubIndex(0);
      setSubProgress(0);
      segStartRef.current = Date.now();
      subStartRef.current = Date.now();
    } else if (roundIndex + 1 < rounds.length) {
      if (chimeEnabled) playChime('segment');
      setRoundIndex(roundIndex + 1);
      setSegmentIndex(0);
      setSegmentElapsed(0);
      setSubIndex(0);
      setSubProgress(0);
      segStartRef.current = Date.now();
      subStartRef.current = Date.now();
    } else {
      if (chimeEnabled) playChime('completion');
      setIsPlaying(false);
      setCompleted(true);
    }
  }, [round, roundIndex, segmentIndex, rounds.length, chimeEnabled]);

  useEffect(() => {
    if (!isPlaying || !segment) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    segStartRef.current = Date.now() - segmentElapsed * 1000;
    if (segment.type === 'active') {
      subStartRef.current = Date.now() - subProgress * (segment.pattern[subIndex]?.duration || 1) * 1000;
    }

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const segElapsed = (now - segStartRef.current) / 1000;
      setSegmentElapsed(segElapsed);
      setTotalSeconds(prev => prev + 0.05);

      // For active segments, advance sub-pattern
      if (segment.type === 'active') {
        const subPhase = segment.pattern[subIndex];
        const subElapsed = (now - subStartRef.current) / 1000;
        const sp = Math.min(subElapsed / subPhase.duration, 1);
        setSubProgress(sp);
        if (subElapsed >= subPhase.duration) {
          setSubIndex((subIndex + 1) % segment.pattern.length);
          setSubProgress(0);
          subStartRef.current = now;
        }
      }

      // End of segment?
      if (segElapsed >= segment.duration) {
        advance();
      }
    }, 50);

    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, segmentIndex, subIndex, roundIndex]);

  const togglePlay = () => {
    if (completed) reset();
    if (!isPlaying) {
      segStartRef.current = Date.now() - segmentElapsed * 1000;
      subStartRef.current = Date.now();
      // Unlock Web Audio in direct response to this click — required by iOS.
      unlockChimeAudio();
    }
    setIsPlaying(!isPlaying);
  };

  const reset = () => {
    setIsPlaying(false);
    setRoundIndex(0);
    setSegmentIndex(0);
    setSegmentElapsed(0);
    setSubIndex(0);
    setSubProgress(0);
    setTotalSeconds(0);
    setCompleted(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const skipSegment = () => {
    if (!isPlaying && !segment) return;
    advance();
  };

  // Compute circle scale + colors based on segment type
  const { scale, color, mainLabel, secondaryLabel } = useMemo(() => {
    if (completed) return { scale: 0.6, color: COLOR_HOLD, mainLabel: 'Complete', secondaryLabel: '' };
    if (!isPlaying && segmentElapsed === 0) return { scale: 0.6, color: segment?.color || COLOR_IN, mainLabel: 'Ready', secondaryLabel: '' };
    if (!segment) return { scale: 0.6, color: COLOR_IN, mainLabel: '—', secondaryLabel: '' };

    if (segment.type === 'active') {
      const sub = segment.pattern[subIndex];
      const sname = sub.name.toLowerCase();
      const s = sname === 'inhale' ? 0.6 + 0.4 * subProgress : 1.0 - 0.4 * subProgress;
      return { scale: s, color: sub.color, mainLabel: sub.name, secondaryLabel: segment.label };
    }
    if (segment.type === 'hold') {
      return { scale: segment.breath === 'full' ? 1.0 : 0.55, color: segment.color, mainLabel: segment.label, secondaryLabel: 'Hold' };
    }
    // recover
    // Use a slow ambient pulse: 4s in / 6s out approx based on elapsed within segment
    const t = (segmentElapsed % 10) / 10;
    const s = t < 0.4 ? 0.6 + 0.3 * (t / 0.4) : 0.9 - 0.3 * ((t - 0.4) / 0.6);
    return { scale: s, color: segment.color, mainLabel: 'Recover', secondaryLabel: 'Slow & natural' };
  }, [completed, isPlaying, segmentElapsed, segment, subIndex, subProgress]);

  const segCountdown = segment ? Math.max(0, Math.ceil(segment.duration - segmentElapsed)) : 0;
  const transitionDur = segment?.type === 'active'
    ? (segment.pattern[subIndex]?.duration || 1) * 0.8
    : 1.2;
  const formattedTotal = `${Math.floor(totalSeconds / 60)}:${String(Math.floor(totalSeconds % 60)).padStart(2, '0')}`;
  const formattedTotalTarget = `${Math.floor(totalDurationSec / 60)}:${String(Math.floor(totalDurationSec % 60)).padStart(2, '0')}`;

  return (
    <>
      <BreathingCircle
        color={color}
        scale={scale}
        transitionDuration={transitionDur}
        primaryLabel={mainLabel}
        secondaryLabel={secondaryLabel}
        countdown={isPlaying && segment && segment.type !== 'recover' ? segCountdown : null}
      />

      {/* Segment instruction */}
      {!completed && segment && (
        <p
          className="font-sans text-sm sm:text-base text-text-muted text-center max-w-md px-6 mt-2 mb-4 leading-relaxed animate-fade-up"
          data-testid="segment-instruction"
        >
          {segment.instruction}
        </p>
      )}
      {completed && (
        <p className="font-serif text-xl text-dark text-center max-w-md px-6 mt-2 mb-4 leading-relaxed">
          Practice complete. Let yourself rest for a moment.
        </p>
      )}

      <ControlsWithSkip
        isPlaying={isPlaying}
        onTogglePlay={togglePlay}
        onReset={reset}
        onSkip={skipSegment}
        disableSkip={completed || !segment}
        chimeEnabled={chimeEnabled}
        onToggleChime={toggleChime}
      />

      {/* Stats */}
      <div className="flex items-center gap-6 sm:gap-8 mt-8 animate-fade-up delay-300" data-testid="breathwork-stats">
        <Stat label="Round" value={`${roundIndex + 1}/${rounds.length}`} testId="round-count" />
        <Divider />
        <Stat label="Phase" value={segment ? segment.label : '—'} valueClass="text-base" testId="phase-label" />
        <Divider />
        <Stat label="Time" value={`${formattedTotal} / ${formattedTotalTarget}`} valueClass="text-base" testId="total-time" />
      </div>

      {/* Round / segment progress timeline */}
      <RoundTimeline
        rounds={rounds}
        roundIndex={roundIndex}
        segmentIndex={segmentIndex}
        segmentProgress={segment ? Math.min(segmentElapsed / segment.duration, 1) : 0}
        completed={completed}
      />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SHARED VISUAL COMPONENTS
// ────────────────────────────────────────────────────────────────────────────

function BreathingCircle({ color, scale, transitionDuration, primaryLabel, secondaryLabel, countdown }) {
  const t = `transform ${transitionDuration * 0.8}s cubic-bezier(0.4, 0, 0.2, 1)`;
  return (
    <div className="relative w-72 h-72 sm:w-80 sm:h-80 mb-6 animate-fade-up delay-100">
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.06] pointer-events-none">
        <svg viewBox="0 0 200 200" width="320" height="320" style={{ animation: 'sacredRotate 120s linear infinite' }}>
          <circle cx="100" cy="100" r="95" fill="none" stroke={COLOR_IN} strokeWidth="0.4" />
          <circle cx="100" cy="100" r="85" fill="none" stroke={COLOR_HOLD} strokeWidth="0.3" />
          <polygon points="100,5 195,145 5,145" fill="none" stroke={COLOR_IN} strokeWidth="0.3" />
          <polygon points="100,195 5,55 195,55" fill="none" stroke={COLOR_IN} strokeWidth="0.3" />
        </svg>
      </div>
      <div className="absolute inset-0 rounded-full" style={{
        transform: `scale(${scale * 1.15})`,
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        transition: t,
      }} />
      <div className="absolute inset-4 rounded-full" style={{
        transform: `scale(${scale * 1.05})`,
        border: `1px solid ${color}30`,
        boxShadow: `0 0 40px ${color}15, inset 0 0 30px ${color}08`,
        transition: `${t}, border-color 0.5s ease, box-shadow 0.5s ease`,
      }} />
      <div
        className="absolute inset-10 rounded-full flex items-center justify-center"
        data-testid="breathing-circle"
        style={{
          transform: `scale(${scale})`,
          background: `radial-gradient(circle at 40% 35%, ${color}25, ${color}08 60%, transparent)`,
          border: `1.5px solid ${color}40`,
          boxShadow: `0 0 60px ${color}20, inset 0 0 40px ${color}10`,
          transition: `${t}, background 0.8s ease, border-color 0.5s ease, box-shadow 0.5s ease`,
        }}
      >
        <div className="text-center px-4">
          {secondaryLabel && (
            <p className="font-sans text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: `${color}cc` }}>
              {secondaryLabel}
            </p>
          )}
          <p
            className="font-serif text-2xl sm:text-3xl tracking-wide"
            style={{ color, transition: 'color 0.5s ease' }}
            data-testid="phase-label-inner"
          >
            {primaryLabel}
          </p>
          {countdown !== null && countdown !== undefined && (
            <p
              className="font-sans text-4xl font-light mt-1"
              style={{ color, transition: 'color 0.5s ease' }}
              data-testid="phase-countdown"
            >
              {countdown}
            </p>
          )}
        </div>
      </div>
      <div className="absolute rounded-full pointer-events-none" style={{
        inset: '40%',
        transform: `scale(${scale * 0.9})`,
        border: `0.5px solid ${color}20`,
        transition: t,
      }} />
    </div>
  );
}

function Controls({ isPlaying, onTogglePlay, onReset }) {
  return (
    <div className="flex items-center gap-4 animate-fade-up delay-200" data-testid="breathwork-controls">
      <Button
        onClick={onReset}
        variant="outline"
        data-testid="reset-breathwork-button"
        className="h-12 w-12 rounded-full border-black/10 bg-bone/40 hover:bg-bone"
      >
        <RotateCcw size={18} />
      </Button>
      <Button
        onClick={onTogglePlay}
        data-testid="play-pause-breathwork-button"
        className="h-16 w-16 rounded-full bg-dark hover:bg-dark/90 text-bone shadow-lg btn-hover"
      >
        {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
      </Button>
      <Button
        onClick={() => { unlockChimeAudio(); playChime('segment'); }}
        variant="outline"
        data-testid="test-chime-button"
        title="Test the chime sound"
        aria-label="Test the chime sound"
        className="h-12 px-4 rounded-full border-black/10 bg-bone/40 hover:bg-bone font-sans text-[10px] tracking-[0.2em] uppercase text-text-muted"
      >
        Test
      </Button>
    </div>
  );
}

function ControlsWithSkip({ isPlaying, onTogglePlay, onReset, onSkip, disableSkip, chimeEnabled, onToggleChime }) {
  return (
    <div className="flex items-center gap-4 animate-fade-up delay-200" data-testid="breathwork-controls">
      <Button
        onClick={onReset}
        variant="outline"
        data-testid="reset-breathwork-button"
        className="h-12 w-12 rounded-full border-black/10 bg-bone/40 hover:bg-bone"
      >
        <RotateCcw size={18} />
      </Button>
      <Button
        onClick={onTogglePlay}
        data-testid="play-pause-breathwork-button"
        className="h-16 w-16 rounded-full bg-dark hover:bg-dark/90 text-bone shadow-lg btn-hover"
      >
        {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
      </Button>
      <Button
        onClick={onSkip}
        variant="outline"
        disabled={disableSkip}
        data-testid="skip-segment-button"
        title="Skip to next segment"
        className="h-12 w-12 rounded-full border-black/10 bg-bone/40 hover:bg-bone disabled:opacity-40"
      >
        <SkipForward size={18} />
      </Button>
      <Button
        onClick={() => { unlockChimeAudio(); playChime('segment'); }}
        variant="outline"
        data-testid="test-chime-button"
        title="Test the chime sound"
        aria-label="Test the chime sound"
        className="h-12 px-4 rounded-full border-black/10 bg-bone/40 hover:bg-bone font-sans text-[10px] tracking-[0.2em] uppercase text-text-muted"
      >
        Test
      </Button>
      <Button
        onClick={onToggleChime}
        variant="outline"
        data-testid="toggle-chime-button"
        title={chimeEnabled ? 'Mute transition chime' : 'Enable transition chime'}
        aria-label={chimeEnabled ? 'Mute transition chime' : 'Enable transition chime'}
        className="h-12 w-12 rounded-full border-black/10 bg-bone/40 hover:bg-bone"
      >
        {chimeEnabled ? <Bell size={18} className="text-gold" /> : <BellOff size={18} className="text-text-muted" />}
      </Button>
    </div>
  );
}

function Stat({ label, value, valueClass = '', testId }) {
  return (
    <div className="text-center">
      <p className="font-sans text-xs text-text-muted tracking-wider uppercase">{label}</p>
      <p className={`font-serif text-2xl text-dark ${valueClass}`} data-testid={testId}>{value}</p>
    </div>
  );
}

function Divider() {
  return <div className="w-px h-8 bg-black/10" />;
}

// Visual timeline of all rounds + segments + progress
function RoundTimeline({ rounds, roundIndex, segmentIndex, segmentProgress, completed }) {
  return (
    <div className="w-full max-w-3xl mt-8 px-4 sm:px-6 animate-fade-up delay-400" data-testid="round-timeline">
      <div className="flex flex-col gap-3">
        {rounds.map((r, ri) => {
          const isCurrentRound = ri === roundIndex && !completed;
          const isPastRound = ri < roundIndex || completed;
          return (
            <div key={ri} className="flex items-center gap-3">
              <span
                className={`shrink-0 font-sans text-[10px] tracking-[0.25em] uppercase w-20 ${
                  isCurrentRound ? 'text-dark' : isPastRound ? 'text-moss' : 'text-text-muted/60'
                }`}
              >
                {r.label}
              </span>
              <div className="flex-1 flex gap-1">
                {r.segments.map((seg, si) => {
                  const isCurrent = isCurrentRound && si === segmentIndex;
                  const isDone = isPastRound || (isCurrentRound && si < segmentIndex);
                  const fill = isDone ? 100 : isCurrent ? Math.round(segmentProgress * 100) : 0;
                  return (
                    <div
                      key={si}
                      className="flex-1 h-2 rounded-full bg-bone/60 overflow-hidden border border-black/5"
                      data-testid={`segment-bar-r${ri}-s${si}`}
                      title={`${seg.label} • ${seg.duration}s`}
                    >
                      <div
                        className="h-full"
                        style={{
                          width: `${fill}%`,
                          background: seg.color,
                          transition: 'width 0.2s linear, background 0.3s ease',
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-5">
        <LegendDot color={COLOR_IN} label="Active" />
        <LegendDot color={COLOR_REST} label="Exhale Hold" />
        <LegendDot color={COLOR_IN} label="Inhale Hold" />
        <LegendDot color={COLOR_HOLD} label="Recovery" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <span className="font-sans text-[10px] text-text-muted tracking-wider uppercase">{label}</span>
    </div>
  );
}
